import { PriceLabelEnum } from '@/enums/PriceLabelEnum'
import { formatCurrency } from '@/lib/currency/format-currency'
import { formatDate } from '@/lib/date/format-date'

import type { Currency } from '@/config/tenants/types'
import type { getBffPrices } from '@/helpers/getBffPrices'

/**
 * Hook to determine all markers for prices
 * - Labels
 * Starting with appropriations and green ordination labels - done
 * Price List, Agreements and Campaign
 * - Tags for Save sum / percentage
 * - Price color indicators
 */
const productLabel = {
  appropriation: 'appropriation',
  greenOrdination: 'greenOrdination',
  positiveList: 'positiveList',
  agreement: 'agreement',
  campaign: 'campaign',
  mixSafe: 'mixAndSave',
  volumePrice: 'volumeDiscount',
  quantity: 'quantityPrice',
  contacted: 'contractedProducts',
  more: 'buyMore',
  minSumSave: 'minSumSave',
  minPercentSave: 'minPercentSave',
}
const productColorClassLabel = {
  petrol: 'petrolLabel',
  red: 'redLabel',
}

const DEFAULT_END_DATE = new Date(new Date().setFullYear(new Date().getFullYear() + 80))

export const getPriceMarkers = (
  priceData: ReturnType<typeof getBffPrices> | null,
  currency: Currency,
  locale: string
) => {
  const productPriceType = priceData?.priceType
  const productRowDiscountType = priceData?.discountRowType
  const percent = priceData?.discountPercentage
  const hasEndDate = priceData?.endDate && formatDate(new Date(priceData.endDate)) < formatDate(DEFAULT_END_DATE)
  const minApplicableQuantity =
    priceData?.minimumQuantity && priceData.minimumQuantity > 1 ? priceData.minimumQuantity : null
  const potentialDiscountsValue = priceData?.fixPrice ?? null
  const getCampaignPromoLabel = () => {
    if (hasEndDate) {
      return productLabel.more
    } else {
      return productLabel.quantity
    }
  }
  const getLabelKey = () => {
    switch (productPriceType) {
      case PriceLabelEnum.APPROPRIATION:
        if (productRowDiscountType === PriceLabelEnum.GREEN_ORDINATION) {
          return productLabel.greenOrdination
        }

        return productLabel.appropriation
      case PriceLabelEnum.POSITIVE_LIST:
        return productLabel.positiveList
      case PriceLabelEnum.AGREEMENT:
        if (productRowDiscountType === PriceLabelEnum.AGREEMENT_GROUP) {
          return null
        }

        return productLabel.agreement
      case PriceLabelEnum.CAMPAIGN:
        if (productRowDiscountType !== PriceLabelEnum.CAMPAIGN_PART) {
          return getCampaignPromoLabel()
        }

        return productLabel.campaign
      default:
        return null
    }
  }
  const getLabelClass = () => {
    if (productPriceType) {
      switch (productPriceType) {
        case PriceLabelEnum.APPROPRIATION:
          return productColorClassLabel.petrol
        case PriceLabelEnum.CAMPAIGN:
          return productColorClassLabel.red
        default:
          return null
      }
    }

    return null
  }

  const showCampaignLabel = getLabelKey() === PriceLabelEnum.CAMPAIGN && minApplicableQuantity == null

  const showMinSaveLabel = minApplicableQuantity != null && minApplicableQuantity > 1
  const getMinSaveLabel = () => {
    if (minApplicableQuantity) {
      if (percent) {
        return `${percent}`
      }
      if (potentialDiscountsValue) {
        return formatCurrency(potentialDiscountsValue, currency, locale)
      }
    }

    return null
  }
  const getSaveLabel = () => {
    if (showCampaignLabel && priceData?.discountAppliedId) {
      if (percent) {
        return `${percent}`
      }
      if (priceData?.discountValue) {
        return formatCurrency(priceData?.discountValue, currency, locale)
      }
    }

    return null
  }

  const saveValue = priceData?.fullValue && priceData?.value && priceData.fullValue - priceData.value > 0

  const fullPrice =
    priceData?.priceType === PriceLabelEnum.CAMPAIGN && saveValue
      ? formatCurrency(priceData.fullValue, currency, locale)
      : null
  const totalPrice = priceData?.totalPrice != null && formatCurrency(priceData.totalPrice, currency, locale)
  const totalFullPrice =
    priceData?.priceType === PriceLabelEnum.CAMPAIGN && saveValue
      ? formatCurrency(priceData?.fullTotalPrice, currency, locale)
      : null
  const labelText = getLabelKey() ? `product.discountLabel.${getLabelKey()}` : null
  const labelStyleClass = getLabelClass()
  const saveLabel = getSaveLabel()
  const minSaveLabel = showMinSaveLabel ? getMinSaveLabel() : null
  const totalFullPriceFromQuantity =
    productPriceType === PriceLabelEnum.CAMPAIGN && priceData && priceData?.fullTotalPrice > priceData?.totalPrice
      ? formatCurrency(priceData?.fullTotalPrice, currency, locale)
      : null

  return {
    labelText,
    labelStyleClass,
    fullPrice,
    saveLabel,
    minSaveLabel,
    totalFullPrice,
    totalPrice,
    totalFullPriceFromQuantity,
  }
}
